import {
    inject,
    Injectable
} from '@angular/core';

import {
    BedzzleCommonFolio,
    BedzzleCommonFoliosResponse,
    BedzzleCommonTransaction,
    BedzzleCommonTransactionResponse
} from 'src/app/features/digital-payments';

import {
    ConfigurationManagerService,
    EntityManagerService
} from 'src/app/core';

import {
    PaymentMethods
} from 'tilby-models';

export type EricsoftApiConfig = {
    ip_address: string;
    port: number;
    username: string;
    password: string;
    property_id: string;
}

export class EricsoftApi {
    private apiUrl: string;

    constructor(
        ipAddress: string,
        port: number,
        private username: string,
        private password: string,
        private propertyId: string
    ) {
        if (ipAddress.includes('/')) {
            this.apiUrl = ipAddress;

            if (!this.apiUrl.endsWith('/')) {
                this.apiUrl += '/';
            }
        } else {
            this.apiUrl = `http://${ipAddress}:${port}/TbyApi/application/`;
        }
    }

    private async sendApiRequest(endpointUrl: string, method: string, data?: any, params: any = {}): Promise<any> {
        const headers = new Headers({
            'Authorization': `Basic ${Buffer.from(`${this.username}:${this.password}`).toString('base64')}`,
        });

        if (data) {
            headers.append('Content-Type', 'application/json');
        }

        const url = new URL(endpointUrl, this.apiUrl);

        for (const key in params) {
            url.searchParams.set(key, params[key]);
        }

        const response = await fetch(url, {
            method: method,
            headers: headers,
            body: data ? JSON.stringify(data) : null,
            signal: AbortSignal.timeout(60000)
        }).catch(err => {
            throw undefined;
        });

        if (!response.ok) {
            throw response;
        }

        return response.json();
    }

    public getPropertyId(): string {
        return this.propertyId;
    }

    public async getRoomFolios(): Promise<BedzzleCommonFolio[]> {
        const response = await this.sendApiRequest('room_folios', 'GET', null, { propertyId: this.propertyId }) as BedzzleCommonFoliosResponse;

        return response.folios;
    }

    public async getMiscSalesFolios(): Promise<BedzzleCommonFolio[]> {
        const response = await this.sendApiRequest('misc_sales_folios', 'GET', null, { propertyId: this.propertyId }) as BedzzleCommonFoliosResponse;

        return response.folios;
    }

    public async createTransaction(transactionData: BedzzleCommonTransaction): Promise<any> {
        const response = await this.sendApiRequest('transactions', 'POST', { ...transactionData, propertyId: this.propertyId }) as BedzzleCommonTransactionResponse

        return response;
    }

    public async rollbackTransaction(outletId: string, propertyId: string, orderId: string): Promise<{ status: string }> {
        const response = await this.sendApiRequest('transactions', 'DELETE', {
            outletId: outletId,
            orderId: orderId,
            propertyId: propertyId
        });

        return response;
    }
}

const configAttributes: Array<keyof EricsoftApiConfig> = ['ip_address', 'port', 'username', 'password', 'property_id'];

@Injectable({
    providedIn: 'root'
})
export class EricsoftPMSApiService {
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly entityManagerService = inject(EntityManagerService);

    public getLegacyConfig(): EricsoftApiConfig {
        const config: any = {};

        for (const key of configAttributes) {
            config[key] = this.configurationManagerService.getPreference(`ericsoft_pms.${key}`) || '';
        }

        config['port'] = parseInt(config['port']) || 5000;

        return <EricsoftApiConfig>config;
    }

    public async setupApi(paymentMethod: PaymentMethods, config: EricsoftApiConfig) {
        Object.assign(paymentMethod, {
            configuration: {
                ...config
            }
        });

        try {
            await this.entityManagerService.paymentMethods.putOneOnline(paymentMethod);
        } catch (err) {
            throw 'SETUP_FAILED';
        }
    }

    private checkConfiguration(config: EricsoftApiConfig) {
        for (const key of configAttributes) {
            if (!config[key]) {
                throw `MISSING_${key.toUpperCase()}`;
            }
        }
    }

    public async getApiInstance(paymentMethod: PaymentMethods) {
        let config = paymentMethod.configuration || {} as EricsoftApiConfig;

        try {
            this.checkConfiguration(config);
        } catch (err) {
            //TODO: Migration logic, remove this in the future
            const ericsoftPaymentMethods = await this.entityManagerService.paymentMethods.fetchCollectionOffline({ payment_method_type_id: 36 });
            const currentPayment = ericsoftPaymentMethods.find((method) => method.id === paymentMethod.id);

            //If the current payment method is not the first ericsoft payment method, throw and don't migrate from legacy config    
            if (currentPayment !== ericsoftPaymentMethods[0]) {
                throw err;
            }

            config = this.getLegacyConfig();

            this.checkConfiguration(config);

            await this.setupApi(paymentMethod, config);
        }

        return new EricsoftApi(config['ip_address'], config['port'] || 5000, config['username'], config['password'], config['property_id']);
    }
}